/**
* React component responsible for rendering a navbar.
*
* Copyright (c) 2020 Coursera
*/
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames'
import { Link } from 'gatsby'
import Grid from '@material-ui/core/Grid';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Button from 'components/theme/Button'
import IconButton from '@material-ui/core/IconButton';
import MenuIcon from '@material-ui/icons/Menu';
import MenuItem from '@material-ui/core/MenuItem'
import Menu from '@material-ui/core/Menu';
import Backdrop from '@material-ui/core/Backdrop';
import styles from './Navbar.module.scss'

const _stripTrailingSlash = (url = '') => (
  (url?.length > 0 && url !== '/') ? url.replace(/\/$/, '') : url
)

const Navbar = ({
  logoUrl, location, logo, alt, backgroundColor, buttons,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [drop, setDrop] = React.useState(false)
  const open = Boolean(anchorEl)
  const pathname = _stripTrailingSlash(location?.pathname)

  const handleMenu = useCallback((event) => {
    setAnchorEl(event.currentTarget);
    setDrop(!drop)
  }, [drop])

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setDrop(false)
  }, [setDrop, setAnchorEl])

  const Buttons = buttons.map(({
    variant,
    text,
    href,
  }) => (
    <Grid key={`navButton-${href}`} item>
      <Link to={href}>
        <Button
          variant={(pathname === href) ? 'primary' : variant}
          size="small">
          {text}
        </Button>
      </Link>
    </Grid>
  ), [buttons])

  const MenuItems = buttons.map(({
    href,
    text,
  }) => {
    const menuLinkClass = cx('flex-1', {
      blue: pathname === href,
      black: pathname !== href,
    })

    return (
      <MenuItem key={`menuItem-${href}`} onClick={handleClose}>
        <Link to={href} className={cx(menuLinkClass)}>{text}</Link>
      </MenuItem>
    )
  })

  const NavMenu = (
    buttons.length > 0
      ? (
        <Backdrop className={styles.menuBackdrop} open={open} onClick={handleClose}>
          <Menu
            id="menu-appbar"
            classes={{ paper: styles.menuPaper }}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={open}
            onClose={handleClose}>
            {MenuItems}
          </Menu>
        </Backdrop>
      ) : null
  )

  return (
    <AppBar
      style={{ backgroundColor }}
      position="relative"
      className={styles.navbar}
      elevation={0}>
      <Toolbar className={styles.toolbar}>
        <div className={cx('d-flex align-items-center')}>
          {NavMenu ? (
            <IconButton
              edge="start"
              className={styles.menuIcon}
              aria-label="menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}>
              <MenuIcon />
            </IconButton>
          ) : null}
          <Link to={logoUrl || '/'}>
            <img
              src={logo}
              alt={alt}
              className={cx(styles.logo, {
                [styles.logoWithMenu]: !!NavMenu,
              })} />
          </Link>
          {NavMenu}
        </div>
        <Grid container spacing={2} className={styles.navLinks} justify="flex-end">
          {Buttons}
        </Grid>
      </Toolbar>
    </AppBar>
  )
}

Navbar.defaultProps = {
  location: {},
  backgroundColor: '#fff',
  alt: 'coursera logo',
  buttons: [],
}

Navbar.propTypes = {
  logoUrl: PropTypes.string,
  location: PropTypes.object,
  backgroundColor: PropTypes.string,
  logo: PropTypes.string,
  alt: PropTypes.string,
  buttons: PropTypes.array,
}

export default Navbar;
